import React, { Suspense, useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl';

import Lozenge from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { CardAction } from '@atlaskit/smart-card';
import {
	SearchConfluenceSubtype,
	type SearchResultType,
} from '@atlassian/search-client/common/types';
import {
	type ActivityItemEdge,
	type ActivityNode,
	type ExternalDocument,
	type ExternalPullRequest,
	type ExternalRemoteLink,
	isDocumentActivity,
	isGraphActivity,
	isGraphPullRequestActivity,
	isGraphWorkItemActivity,
	isLoomActivity,
	isRemoteLinkActivity,
	type QuickFind1PEntity,
	type QuickFindAssetsEntity,
	type QuickFindGithubEntity,
	type QuickFindGraphEntity,
	type QuickFindGraphWorkItemEntity,
	type QuickFindLoomEntity,
} from '@atlassian/search-client/recent-activities/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { is3pProductKeyForAnalytics } from '../../../../common/constants/is3p-product-key-for-analytics';
import {
	ProductKeys,
	ProductKeys1PForAnalytics,
	type ProductKeys3P,
} from '../../../../common/constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../../common/constants/schemas/3p-config';
import {
	getDocumentSubtypeLabel,
	getRemoteLinkSubtypeLabel,
} from '../../../../common/ui/3p-result-labeling';
import { EventTypeMessage } from '../../../../common/ui/event-type-message';
import {
	getAssetsEntityIcon,
	getAtlasEntityIcon,
	getConfluenceEntityIcon,
	getJiraEntityIcon,
} from '../../../../common/ui/product-entity-icon';
import { ProductIcon } from '../../../../common/ui/product-icon';
import { useItemsAnalyticsContext } from '../../../../common/ui/quick-find/items-analytics-provider';
import {
	SearchResult,
	type SearchResultEventArguments,
} from '../../../../common/ui/quick-find/search-dialog-result';
import { type SearchMetadataPart } from '../../../../common/ui/search-metadata';
import { SearchResultType as SearchResultTypeText } from '../../../../common/ui/search-result-type';
import {
	serveGithubWorkItemsRecentsExperiment,
	serveGithubRecentsExperiment,
} from '../../../../common/utils/experiments';
import { getProductDisplayNameFromProviderId } from '../../../../common/utils/get-product-display-name-from-provider-id';
import { getProductFromProviderId } from '../../../../common/utils/get-product-from-provider-id';
import { getProductFromThirdPartyId } from '../../../../common/utils/get-product-from-third-party-id';
import { normalizeWorkItemStatusLabel } from '../../../../common/utils/normalize-work-item-status-label';
import {
	getPullRequestStatusAppearance,
	getPullRequestStatusMessage,
} from '../../../../common/utils/pull-request-status';
import { calculateMinuteDifference } from '../../../../common/utils/quick-find/analytics';
import { type State } from '../../../../common/utils/quick-find/state';
import {
	getResultConnectorSource,
	isResultSmartLink,
} from '../../../../common/utils/third-party-utils';
import { getWorkItemStatusAppearance } from '../../../../common/utils/work-item-status';
import { ErrorBoundary } from '../../../../controllers/error-boundary';
import { type QuickFindResultEvent } from '../../../../controllers/quick-find/utils';
import {
	useBootstrap,
	usePrimaryProduct,
	useSearchSessionId,
} from '../../../../controllers/store/bootstrap';
import { useQuickFindActions, useQuickFindQuery } from '../../../../controllers/store/quick-find';

import { messages } from './messages';

const HoverCard = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_smart-card/hover-card" */ '@atlaskit/smart-card/hover-card'
	).then((mod) => ({ default: mod.HoverCard })),
);

const labelContainer = xcss({
	'::first-letter': {
		textTransform: 'capitalize',
	},
});

const DraftBadge: React.FC<{ message: React.ComponentProps<typeof FormattedMessage> }> = ({
	message,
}) => {
	return (
		<Inline>
			<Lozenge appearance="neutral">
				<FormattedMessage {...message} />
			</Lozenge>
		</Inline>
	);
};

export const getConfluenceTitleMetadataParts = (
	data: NonNullable<ActivityNode<QuickFind1PEntity>['object']['data']>,
): SearchMetadataPart[] | undefined => {
	if ((data?.pageStatus === 'DRAFT' || data?.blogStatus === 'DRAFT') && data?.subtype !== 'LIVE') {
		return [['confluence-draft', <DraftBadge message={messages.draftLabel} />]];
	}
};

const mapConfluenceActivityToSearchResult = (
	activity: ActivityNode<QuickFind1PEntity>,
	query: string,
): Result | undefined => {
	if (!activity.object.data) {
		return;
	}
	const id = activity.object.id;
	const { title, space, links } = activity.object.data;

	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const titleMetadataParts = getConfluenceTitleMetadataParts(activity.object.data);

	const isLiveDoc =
		activity.object.data?.subtype === SearchConfluenceSubtype.LIVE &&
		fg('confluence_live_pages_open_beta_trait_opted_in');

	const icon = fg('rovo_search_confluence_icons') ? (
		getConfluenceEntityIcon({
			iconUrl: activity.object.data.iconData,
			type: activity.object.type,
			isLiveDoc,
		})
	) : (
		<ProductIcon size="small" product={ProductKeys.Confluence} />
	);

	const resultType = isLiveDoc ? <FormattedMessage {...messages.liveDoc} /> : activity.object.type;
	const type = [
		'confluence-type',
		<Box xcss={labelContainer}>
			<SearchResultTypeText
				type={activity.object.type as SearchResultType}
				defaultMessage={resultType}
				subtype={activity.object.data?.subtype}
			/>
		</Box>,
	] satisfies SearchMetadataPart;
	const spaceName = space?.name || '';
	const metadata = [type, spaceName] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const urlBase = links?.base;
	const urlWebUi = links?.webUi;
	const url = urlBase && urlWebUi && `${urlBase}${urlWebUi}`;

	const spaceId = space?.id;
	const analyticsMetadata = {
		contentId: id,
		containerId: spaceId,
		contentType: `${ProductKeys.Confluence} ${activity.object.type}`,
		product: ProductKeys.Confluence,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	if (!title || !url) {
		return undefined;
	}

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
		titleMetadataParts,
	};
};

// export for testing purposes
export const mapJiraActivityToSearchResult = (
	activity: ActivityNode<QuickFind1PEntity>,
	query: string,
): Result | undefined => {
	if (!activity.object.data) {
		return;
	}

	const id = activity.object.id;
	const { key, fieldsById, webUrl } = activity.object.data;
	const isIssue = activity.object.type === 'issue';

	const icon = getJiraEntityIcon({
		iconUrl: activity.object.data?.issueType?.avatar?.small,
		type: activity.object.type,
		isIssue,
	});

	const summary = fieldsById?.edges?.[0]?.node?.text;
	const title = key && summary && `${key}: ${summary}`;
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const productMetadata = 'Jira' as const;

	const projectName =
		activity?.object?.data?.fieldsById?.edges?.[0]?.node?.issue?.projectField?.project?.name ??
		null;

	const issueTypeName =
		activity?.object?.data?.fieldsById?.edges?.[0]?.node?.issue?.issueTypeField?.issueType?.name ??
		null;

	const metadata: Result['metadata'] = [productMetadata, issueTypeName, projectName];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Jira} ${activity.object.type}`,
		product: ProductKeys.Jira,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	if (!title || !webUrl) {
		return undefined;
	}

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url: webUrl,
		analyticsMetadata,
		isJiraIssue: isIssue,
	};
};

export const mapAssetsActivityToSearchResult = (
	activity: ActivityNode<QuickFindAssetsEntity>,
	query: string,
): Result | undefined => {
	if (!activity.object.data) {
		return undefined;
	}
	const id = activity.object.id;
	const data = activity.object.data;
	const { assetsLabel: title, displayTypename, links, assetsIcon, schema, objectType } = data;
	const url = links?.web || null;
	if (!title || !url) {
		return undefined;
	}
	const icon = getAssetsEntityIcon({
		iconUrl: assetsIcon?.url48,
		type: activity.object.type,
		size: 'small',
	});
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	// Create metadata with object type and schema information
	const schemaName = schema?.name || objectType?.schema?.name || null;
	const productMetadata = 'Assets';

	const metadata: Result['metadata'] = [
		productMetadata,
		[activity.object.type, <Box xcss={labelContainer}>{displayTypename}</Box>],
		schemaName,
	];

	// Add event type information
	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `assets ${activity.object.type}`,
		// Normalise to the canonical 1P key ('cmdb') so analytics pipelines
		// see a single identifier regardless of whether the activity API
		// returns 'assets' or the ARI resource-owner is 'assets'.
		product: fg('search-page-assets-analytics-consolidation') ? ProductKeys.Assets : 'assets',
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapTownsquareActivityToSearchResult = (
	activity: ActivityNode<QuickFind1PEntity>,
	query: string,
) => {
	if (!activity.object.data) {
		return;
	}
	const id = activity.object.id;
	const { name, key, url } = activity.object.data;

	let icon: React.JSX.Element;
	if (fg('rovo_search_confluence_icons')) {
		icon = getAtlasEntityIcon({
			iconUrl: activity.object.data.iconData,
			type: activity.object.type,
		});
	} else {
		icon = <ProductIcon size="small" product={ProductKeys.Atlas} />;
	}

	const highlightedTitle =
		name && query ? highlightMatchedText(name, query.split(' ')).highlightedTitle : undefined;

	const type = [
		'type',
		<Box xcss={labelContainer}>
			<SearchResultTypeText
				type={activity.object.type as SearchResultType}
				defaultMessage={activity.object.type}
			/>
		</Box>,
	] satisfies SearchMetadataPart;
	const metadata = [type, key ?? null] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	let product: string = ProductKeys.Atlas;
	if (fg('rovo_search_cross_product_url_wrapping')) {
		if (activity.object.type === 'goal') {
			product = ProductKeys1PForAnalytics.AtlasGoals;
		} else if (activity.object.type === 'project') {
			product = ProductKeys1PForAnalytics.AtlasProjects;
		}
	}

	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Atlas} ${activity.object.type}`,
		product,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	if (!name || !url) {
		return undefined;
	}

	return {
		id,
		icon,
		title: name,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapGraphDocumentActivityToSearchResult = (
	activity: ActivityNode<ExternalDocument>,
	query: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	if (!activity.object.data) {
		return;
	}
	const { type, displayName, parent, url, provider } = activity.object.data;
	const id = activity.object.id;
	const providerId = provider?.providerId;

	const product =
		getProductFromProviderId(thirdPartyConfigs, providerId) ||
		getProductFromThirdPartyId({ thirdPartyId: id });

	if (!product) {
		return;
	}

	const subtype = type?.category?.toLowerCase();
	const label = getDocumentSubtypeLabel({
		product,
		subtype,
	});

	const title = displayName;
	if (!title || !url) {
		return undefined;
	}
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const icon = <ProductIcon size="small" product={product as ProductKeys} />;

	const typeLabel = [
		'typeLabel',
		label ? (
			<Box xcss={labelContainer}>
				<FormattedMessage {...label} />
			</Box>
		) : subtype ? (
			<Box xcss={labelContainer}>
				{subtype.charAt(0).toUpperCase() + subtype.substring(1).toLowerCase()}
			</Box>
		) : null,
	] satisfies SearchMetadataPart;

	const parentName = parent?.displayName ?? null;
	const metadata = [typeLabel, parentName] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${product} ${subtype}`,
		product: product,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapGraphRemoteLinkActivityToSearchResult = (
	activity: ActivityNode<ExternalRemoteLink>,
	query: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	if (!activity.object.data) {
		return;
	}
	const { category, displayName, url, provider } = activity.object.data;
	const id = activity.object.id;
	const providerId = provider?.providerId;

	let product;
	let productDisplayName: React.ReactNode = '';
	if (providerId) {
		product =
			getProductFromProviderId(thirdPartyConfigs, providerId) ||
			getProductFromThirdPartyId({
				thirdPartyId: id,
			});
		productDisplayName = getProductDisplayNameFromProviderId(thirdPartyConfigs, providerId) || '';
	}

	if (!product) {
		return;
	}

	const subtype = category?.toLowerCase();
	const label = getRemoteLinkSubtypeLabel({
		product,
		subtype,
	});

	const title = displayName;
	if (!title || !url) {
		return undefined;
	}
	const highlightedTitle =
		title && query ? highlightMatchedText(title, query.split(' ')).highlightedTitle : undefined;

	const icon = <ProductIcon size="small" product={product as ProductKeys} />;

	const productLabel = [
		'productLabel',
		productDisplayName ? <Box xcss={labelContainer}>{productDisplayName}</Box> : null,
	] satisfies SearchMetadataPart;

	const typeLabel = [
		'typeLabel',
		label ? (
			<Box xcss={labelContainer}>
				<FormattedMessage {...label} />
			</Box>
		) : null,
	] satisfies SearchMetadataPart;
	const metadata = [productLabel, typeLabel] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${product} ${subtype}`,
		product: product,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	return {
		id,
		icon,
		title,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapGraphActivityToSearchResult = (
	activity: ActivityNode<QuickFindGraphEntity>,
	query: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
) => {
	if (isDocumentActivity(activity.object)) {
		return mapGraphDocumentActivityToSearchResult(activity, query, thirdPartyConfigs);
	}
	if (isRemoteLinkActivity(activity.object)) {
		return mapGraphRemoteLinkActivityToSearchResult(activity, query, thirdPartyConfigs);
	}
	return undefined;
};

const mapGraphWorkItemActivityToSearchResult = (
	activity: ActivityNode<QuickFindGraphWorkItemEntity>,
	query: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): Result | undefined => {
	if (!activity.object.data) {
		return undefined;
	}

	const id = activity.object.id;
	// `workItemStatus` is a GraphQL alias for `status` (see ExternalWorkItemFragment) to
	// avoid an AGG FieldsConflict with ExternalPullRequest.status (enum vs String).
	const { displayName, url, provider, workItemStatus: status } = activity.object.data;
	const providerId = provider?.providerId;

	let product;
	let productDisplayName: React.ReactNode = '';
	if (providerId) {
		product =
			getProductFromProviderId(thirdPartyConfigs, providerId) ||
			getProductFromThirdPartyId({
				thirdPartyId: id,
			});
		productDisplayName = product && thirdPartyConfigs?.[product as ProductKeys3P]?.displayName;
	}

	if (!product || !displayName || !url) {
		return undefined;
	}

	const icon = <ProductIcon size="small" product={product as ProductKeys} />;
	const highlightedTitle = query
		? highlightMatchedText(displayName, query.split(' ')).highlightedTitle
		: undefined;

	const type = [
		'work-item-type',
		<Box xcss={labelContainer}>
			<SearchResultTypeText
				type={activity.object.type as SearchResultType}
				defaultMessage={activity.object.type}
			/>
		</Box>,
	] satisfies SearchMetadataPart;
	const productLabel = [
		'productLabel',
		productDisplayName ? <Box xcss={labelContainer}>{productDisplayName}</Box> : null,
	] satisfies SearchMetadataPart;
	const metadata = [productLabel, type] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${product} ${activity.object.type}`,
		product,
	};

	const titleMetadataParts: SearchMetadataPart[] = [];

	if (status) {
		// TODO: i18n for work-item statuses is intentionally skipped until we confirm the
		// full set of GitHub work-item status values and whether to share a vocab with PRs.
		// See https://atlassian.slack.com/archives/C050EC6FM28/p1779690735397479
		titleMetadataParts.push([
			'status',
			<Lozenge
				appearance={getWorkItemStatusAppearance(status)}
				testId="work-item-status-lozenge"
			>
				{normalizeWorkItemStatusLabel(status)}
			</Lozenge>,
		]);
	}

	return {
		id,
		icon,
		title: displayName,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
		...(titleMetadataParts.length > 0 ? { titleMetadataParts } : {}),
	};
};

const mapGraphPullRequestActivityToSearchResult = (
	activity: ActivityNode<ExternalPullRequest>,
	query: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): Result | undefined => {
	if (!activity.object.data) {
		return undefined;
	}

	const id = activity.object.id;
	const { displayName, url, provider, status: prStatus } = activity.object.data;
	const providerId = provider?.providerId;

	let product;
	let productDisplayName: React.ReactNode = '';
	if (providerId) {
		product =
			getProductFromProviderId(thirdPartyConfigs, providerId) ||
			getProductFromThirdPartyId({
				thirdPartyId: id,
			});
		productDisplayName = product && thirdPartyConfigs?.[product as ProductKeys3P]?.displayName;
	}

	if (!product || !displayName || !url) {
		return undefined;
	}

	const icon = <ProductIcon size="small" product={product as ProductKeys} />;
	const highlightedTitle = query
		? highlightMatchedText(displayName, query.split(' ')).highlightedTitle
		: undefined;

	const type = [
		'pull-request-type',
		<Box xcss={labelContainer}>
			<SearchResultTypeText
				type={activity.object.type as SearchResultType}
				defaultMessage={activity.object.type}
			/>
		</Box>,
	] satisfies SearchMetadataPart;
	const productLabel = [
		'productLabel',
		productDisplayName ? <Box xcss={labelContainer}>{productDisplayName}</Box> : null,
	] satisfies SearchMetadataPart;
	const metadata = [productLabel, type] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${product} ${activity.object.type}`,
		product,
	};

	const titleMetadataParts: SearchMetadataPart[] = [];

	if (prStatus) {
		titleMetadataParts.push([
			'status',
			<Lozenge
				appearance={getPullRequestStatusAppearance(prStatus)}
				testId="pull-request-status-lozenge"
			>
				<FormattedMessage {...getPullRequestStatusMessage(prStatus)} />
			</Lozenge>,
		]);
	}

	return {
		id,
		icon,
		title: displayName,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
		...(titleMetadataParts.length > 0 ? { titleMetadataParts } : {}),
	};
};

const mapGithubActivityToSearchResult = (
	activity: ActivityNode<QuickFindGithubEntity>,
	query: string,
) => {
	if (!activity.object.data) {
		return;
	}
	const id = activity.object.id;
	const { displayName, url } = activity.object.data;

	if (!displayName || !url) {
		return undefined;
	}

	const icon = <ProductIcon size="small" product={'github' as ProductKeys} />;
	const highlightedTitle =
		displayName && query
			? highlightMatchedText(displayName, query.split(' ')).highlightedTitle
			: undefined;

	const type = [
		'github-type',
		<Box xcss={labelContainer}>
			<SearchResultTypeText
				type={activity.object.type as SearchResultType}
				defaultMessage={activity.object.type}
			/>
		</Box>,
	] satisfies SearchMetadataPart;
	const metadata = [type, 'GitHub'] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${ProductKeys.Github} ${activity.object.type}`,
		product: ProductKeys.Github,
	};

	return {
		id,
		icon,
		title: displayName,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url,
		analyticsMetadata,
	};
};

const mapLoomActivityToSearchResult = (
	activity: ActivityNode<QuickFindLoomEntity>,
	query: string,
): Result | undefined => {
	if (!activity.object.data) {
		return undefined;
	}

	const id = activity.object.id;
	const { name, loomUrl } = activity.object.data;

	if (!name || !loomUrl) {
		return undefined;
	}

	const icon = <ProductIcon size="small" product={ProductKeys.Loom} />;
	const highlightedTitle = query
		? highlightMatchedText(name, query.split(' ')).highlightedTitle
		: undefined;

	const metadata = ['Loom', 'Video'] satisfies SearchMetadataPart[];

	const timestamp = activity.event.timestamp;
	const eventType = activity.event.eventType;
	const rightMetadata = (
		<EventTypeMessage timestamp={timestamp} eventType={eventType} isMyActivityData={true} />
	);
	const highlightedRightMetadata = (
		<Inline alignBlock="center" space="space.100">
			{rightMetadata}
			<ReturnIcon />
		</Inline>
	);

	const analyticsMetadata = {
		contentId: id,
		contentType: `${activity.object.product} ${activity.object.type}`,
		product: activity.object.product,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};

	return {
		id,
		icon,
		title: name,
		highlightedTitle,
		metadata,
		rightMetadata,
		highlightedRightMetadata,
		url: loomUrl,
		analyticsMetadata,
	};
};

type Result = {
	id: string;
	icon: React.JSX.Element;
	title: string;
	highlightedTitle: string | undefined;
	metadata: (string | [string, React.JSX.Element | null] | null)[];
	rightMetadata: React.JSX.Element;
	highlightedRightMetadata: React.JSX.Element;
	url: string;
	analyticsMetadata: {
		minutesSinceActivityTimestamp?: number | null;
		contentId: string;
		contentType: string;
		product: string;
	};
	isJiraIssue?: boolean;
	titleMetadataParts?: SearchMetadataPart[];
};

export const mapActivityToSearchResult = (
	edge: ActivityItemEdge,
	query: string,
	searchSessionId: string,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
	serveGithubLinksHydrationCohort: boolean,
):
	| {
			url: string;
			minutesSinceActivityTimestamp: number | null;
			id: string;
			icon: React.JSX.Element;
			title: string;
			highlightedTitle: string | undefined;
			metadata: (string | [string, React.JSX.Element | null] | null)[];
			rightMetadata: React.JSX.Element;
			highlightedRightMetadata: React.JSX.Element;
			analyticsMetadata: {
				minutesSinceActivityTimestamp?: number | null;
				contentId: string;
				contentType: string;
				product: string;
			};
			isJiraIssue?: boolean;
			titleMetadataParts?: SearchMetadataPart[];
	  }
	| undefined => {
	const activity = edge.node;
	const product = activity.object.product;
	const timestamp = activity.event.timestamp;
	let result: Result | undefined;

	if (isGraphPullRequestActivity(activity.object) && serveGithubRecentsExperiment()) {
		result = mapGraphPullRequestActivityToSearchResult(activity, query, thirdPartyConfigs);
	} else if (isGraphWorkItemActivity(activity.object) && serveGithubWorkItemsRecentsExperiment()) {
		result = mapGraphWorkItemActivityToSearchResult(activity, query, thirdPartyConfigs);
	} else if (isGraphActivity(activity.object)) {
		result = mapGraphActivityToSearchResult(activity, query, thirdPartyConfigs);
	} else if (product === 'confluence') {
		result = mapConfluenceActivityToSearchResult(activity, query);
	} else if (product === 'jira') {
		result = mapJiraActivityToSearchResult(activity, query);
	} else if (product === 'townsquare') {
		result = mapTownsquareActivityToSearchResult(activity, query);
	} else if (product === 'github' && serveGithubLinksHydrationCohort) {
		result = mapGithubActivityToSearchResult(activity, query);
	} else if (product === 'assets' && fg('assets_rovo_search_all')) {
		result = mapAssetsActivityToSearchResult(activity, query);
	} else if (isLoomActivity(activity.object)) {
		result = mapLoomActivityToSearchResult(activity, query);
	} else {
		result = undefined;
	}

	if (!result) {
		return undefined;
	}

	const urlWithAnalyticsQueryParams = getGenericUrlWithAnalyticsQueryParams({
		url: result.url,
		searchSessionId,
	});

	return {
		...result,
		url: urlWithAnalyticsQueryParams,
		minutesSinceActivityTimestamp: calculateMinuteDifference(timestamp),
	};
};

type Props = {
	result: ActivityItemEdge;
	state: State;
	onClick?: (props: QuickFindResultEvent) => void;
	onContextMenu?: (props: QuickFindResultEvent) => void;
	onHighlighted?: (props: QuickFindResultEvent) => void;
	serveGithubLinksHydration?: boolean;
	resultType?: 'intentRouterWorkItem' | 'recentActivities';
};

export const RecentActivity = ({
	result,
	state,
	onClick,
	onContextMenu,
	onHighlighted,
	serveGithubLinksHydration = false,
	resultType = 'recentActivities',
}: Props): React.JSX.Element | null => {
	const quickFindQuery = useQuickFindQuery();
	const { setHoverCardOpen } = useQuickFindActions();
	const [primaryProduct] = usePrimaryProduct();
	const [searchSessionId] = useSearchSessionId();
	const [{ thirdPartyConfigs }] = useBootstrap();
	const { invalidateItem } = useItemsAnalyticsContext();

	const mappedResult = mapActivityToSearchResult(
		result,
		quickFindQuery,
		searchSessionId,
		thirdPartyConfigs,
		serveGithubLinksHydration,
	);

	const additionalAnalyticsAttributes = useMemo(
		() => ({
			isCached: true,
			metadata: {
				...mappedResult?.analyticsMetadata,
				connectorType: getResultConnectorSource({
					resultNodeId: result.node.object.id,
					thirdPartyConfigs,
					productKey: mappedResult?.analyticsMetadata.product,
					eventType: result.node.event.eventType,
				}),
				...(fg('rovo_search_add_recent_activity_event_type') && {
					recentActivityEventType: result.node.event.eventType,
				}),
			},
			is3pLink: is3pProductKeyForAnalytics(mappedResult?.analyticsMetadata.product),
			is3pSmartLink: isResultSmartLink({
				resultAri: result.node.object.id,
				thirdPartyConfigs,
				productKey: mappedResult?.analyticsMetadata.product,
				eventType: result.node.event.eventType,
			}),
		}),
		[mappedResult, result.node.object.id, thirdPartyConfigs, result.node.event.eventType],
	);

	const onHoverCardVisibilityChange = useCallback(
		(isOpen: boolean) => {
			setHoverCardOpen(result.node.object.id, isOpen);
		},
		[setHoverCardOpen, result.node.object.id],
	);

	const onResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onClick?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
			});

			onHoverCardVisibilityChange(false);
		},
		[additionalAnalyticsAttributes, onClick, state, onHoverCardVisibilityChange],
	);

	const onResultContextMenu = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			data: SearchResultEventArguments,
		) => {
			onContextMenu?.({
				event,
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onContextMenu, state],
	);

	const onResultHighlighted = useCallback(
		(data: SearchResultEventArguments) => {
			onHighlighted?.({
				data,
				state,
				additionalAnalyticsAttributes,
			});
		},
		[additionalAnalyticsAttributes, onHighlighted, state],
	);

	if (!mappedResult) {
		invalidateItem(result.node.object.id);
		return null;
	}

	const enableJiraIssueHoverCard =
		mappedResult.isJiraIssue &&
		primaryProduct === ProductKeys.Jira &&
		fg('enable_hover_card_jira_issues_quick_find');

	const content = (
		<SearchResult
			{...mappedResult}
			resultType={resultType}
			primaryProduct={primaryProduct}
			product={mappedResult.analyticsMetadata.product}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onResultHighlighted={onResultHighlighted}
			minutesSinceActivityTimestamp={mappedResult.minutesSinceActivityTimestamp}
			{...(fg('rovo_search_add_recent_activity_event_type') && {
				recentActivityEventType: result.node.event.eventType,
			})}
			connectorType={additionalAnalyticsAttributes.metadata.connectorType}
		/>
	);

	return enableJiraIssueHoverCard ? (
		<ErrorBoundary fallback={content} section="recent-activity-hover-card">
			<Suspense fallback={content}>
				<HoverCard
					onVisibilityChange={onHoverCardVisibilityChange}
					allowEventPropagation
					url={mappedResult.url}
					actionOptions={{
						hide: false,
						exclude: [CardAction.PreviewAction],
					}}
					role="dialog"
					titleId={mappedResult.title}
					label="quick-find-smart-hover-card"
					shouldRenderToParent
				>
					{content}
				</HoverCard>
			</Suspense>
		</ErrorBoundary>
	) : (
		content
	);
};
